import {
  PROMO_PAYMENT_TYPE,
  VOUCHER_PAYMENT_TYPE,
  CREDIT_PAYMENT_TYPE,
  PAYMENT_SCHEDULE_STATUS,
  loyaltyPaymentTypes,
  GIFT_CARD_PAYMENT_TYPE,
} from 'constants/payment'
import moment from 'moment'
import { groupBy, sortBy, sum } from 'lib/array/arrayUtils'
import { isInstalmentFullyPaidOff } from 'lib/payment/payInInstalmentsUtils'
import config from 'constants/config'
import { getCustomOfferV2Type } from 'checkout/lib/customOffer'

export function getTotalsByItemTypes(order: App.Order) {
  return ({
    accommodationTotal: sum(order.items, item => {
      let itemTotal = item.packagePrice

      if (item.reservation?.surcharge &&
        !item.reservation.surchargePaidDirectToVendor) {
        itemTotal = itemTotal + item.reservation.surcharge
      }

      return itemTotal
    }),
    addonsTotal: sum(order.addonItems, i => i.total),
    experiencesTotal: sum(order.experienceItems, i => i.total),
    insuranceTotal: sum(order.insuranceItems, i => i.total),
    flightsTotal: sum(order.flightItems, i => i.total),
  })
}

export function getTotalsExperiencesTickets(order: App.Order) {
  return sum(order.experienceItems, (i) => i.total)
}

export function getTotalPrice(order: App.Order) {
  return sum(getTotalsByItemTypes(order), total => total)
}

export function getPaymentSummary(payments: Array<App.OrderPayment>, hasDowngrade = false) {
  return sum(payments, payment => {
    if (payment.type === PROMO_PAYMENT_TYPE ||
      payment.type === VOUCHER_PAYMENT_TYPE ||
      payment.status === 'failed' ||
      (payment.amount < 0 && !hasDowngrade)) {
      return 0
    }
    return payment.amount
  })
}

export function getCreditPaymentSummary(payments: Array<App.OrderPayment>, hasDowngrade = false) {
  return sum(payments, payment => {
    if (payment.status === 'failed' ||
      payment.type !== CREDIT_PAYMENT_TYPE ||
      (payment.amount < 0 && !hasDowngrade)) {
      return 0
    }
    return payment.amount
  })
}

export function getGiftCardPaymentSummary(payments: Array<App.OrderPayment>) {
  return sum(payments, payment => {
    if (payment.status === 'failed' || payment.type !== GIFT_CARD_PAYMENT_TYPE) return 0
    return payment.amount
  })
}

export function getLoyaltyPaymentSummary(payments: Array<App.OrderPayment>) {
  return sum(payments, payment => {
    if (payment.status === 'failed' || !loyaltyPaymentTypes.has(payment.type)) return 0
    return payment.amount
  })
}

export function isSuccessfulPromoPayment(payment: App.OrderPayment) {
  return payment.status === 'completed' && payment.type === PROMO_PAYMENT_TYPE
}

export function getPromoPaymentSummary(payments: Array<App.OrderPayment>) {
  return sum(payments, payment => {
    if (isSuccessfulPromoPayment(payment)) {
      return payment.amount
    }
    return 0
  })
}

export function isSuccessfulVoucherPayment(payment: App.OrderPayment) {
  return payment.status === 'completed' && payment.type === VOUCHER_PAYMENT_TYPE
}

export function getVoucherPaymentSummary(payments: Array<App.OrderPayment>) {
  return sum(payments, payment => {
    if (isSuccessfulVoucherPayment(payment)) {
      return payment.amount
    }
    return 0
  })
}

export function getRefundSummary(payments: Array<App.OrderPayment>) {
  return sum(payments, payment => {
    if (payment.type === PROMO_PAYMENT_TYPE || payment.type === VOUCHER_PAYMENT_TYPE) {
      return 0
    }
    if (payment.status === 'failed') {
      return 0
    }
    if (payment.amount > 0) {
      return 0
    }
    return -payment.amount
  })
}

export function getRefundableAmount(payments: Array<App.OrderPayment>) {
  return sum(payments, payment => {
    if (payment.type === PROMO_PAYMENT_TYPE || payment.type === VOUCHER_PAYMENT_TYPE || payment.status === 'failed') {
      return 0
    }
    // N.B. Already refunded amounts are negative payment amounts
    return payment.amount
  })
}

export function getDowngradeRefundSummary(refunds: Array<App.OrderRefund>) {
  return sum(refunds, refund => {
    if (refund.reason === 'Up/Downgrade voucher') {
      return refund.amount
    }
    return 0
  })
}

function getInsuranceOrBookingProtectionAppDiscountTotal(item: App.OrderInsuranceItem | App.OrderBookingProtectionItem) {
  return item.mobilePrice === item.total ? item.publicPrice - item.mobilePrice : 0
}

interface OrderPaymentTotals {
  grandTotal: number
  subTotal: number
  paidTotal: number
  refundedTotal: number
  hasPromoPayment: boolean
  insuranceAppDiscountTotal: number
  payOnPickupFees: number
  customOfferDeposit: number
  bookingProtectionAppDiscountTotal: number
  promoTotal: number
  voucherTotal: number
  commissionTotal: number
  giftCardTotal: number
  creditsTotal: number
  deliveryFeeTotal: number
  paymentGroups: Array<{
    date: moment.Moment
    subTotal: number
    paidTotal: number
    promoTotal: number
    hasPromoPayment: boolean
  }>
  hasDowngradedItem: boolean
  discountAmount: number
  discountPercentage: number
  merchantFee: number
  propertyFees: number
  dueAtProperty: number
  accommodationTotal: number
  mobileAppDiscount: {
    totalAmount: number
    totalPercentage: number
    experiences: { amount: number, percentage: number }
    transfers: { amount: number, percentage: number }
  }
}

export function getPaymentTotals(
  order: App.Order,
  carHireReservationTotalCharge?: App.CarHireReservation['totalCharge'],
): OrderPaymentTotals {
  const merchantFee = sum(order.merchantFees, fee => fee.amount)
  // deposits
  const isDepositOrder = !!order.payments[0]?.depositDetails
  const isDepositBalancePaid = !!order.payments[0]?.depositDetails?.balance_paid_date
  const isInitialDepositOrder = isDepositOrder && !isDepositBalancePaid
  const isCustomOffer = order.customOfferItems.length > 0
  const customOffer = order.customOfferItems[0]?.custom_offer
  const v2Type = isCustomOffer ? getCustomOfferV2Type(order) : undefined
  // v1 customer_deposit_1_amount is stored in cents (divide by 100).
  // v2 manual_deposit_amount is stored in dollars (no division) per api-contracts.md.
  let customOfferDeposit = 0
  if (isCustomOffer) {
    if (v2Type === 'manual') {
      customOfferDeposit = customOffer?.payment_metadata?.payment_flow_v2?.manual_deposit_amount ?? 0
    } else if (customOffer?.payment_metadata?.customer_deposit_1_amount) {
      customOfferDeposit = customOffer.payment_metadata.customer_deposit_1_amount / 100
    }
  }
  let subTotalCalc = 0
  let creditTotalCalc = 0
  let discountAmount = 0
  let discountPercentage = 0
  // instalments
  const isPendingInstalmentOrder = !!order.instalmentDetails && !isInstalmentFullyPaidOff(order.instalmentDetails)

  // reserveForZero
  const isPendingReserveForZero = order.reserveForZeroDetails && !order.reserveForZeroDetails.actualPaymentDate

  // payment schedule
  const isPendingPaymentScheduleOrder = order.paymentScheduleDetails && order.paymentScheduleDetails.status !== PAYMENT_SCHEDULE_STATUS.PAID

  const hasDowngradedItem = [...order.items, ...order.bedbankItems].some((item) => item.isDowngraded || item.downgradedFromId)
  const completedPayments = order.payments.filter(payment => payment.status === 'completed')
  const grouped = groupBy(completedPayments, payment => moment(payment.createdAt).startOf('day').valueOf())
  const paymentGroups = Array.from(grouped.entries()).map(([date, payments]) => {
    const promo = getPromoPaymentSummary(payments)
    const voucher = getVoucherPaymentSummary(payments)
    const hasPromoPayment = payments.some(isSuccessfulPromoPayment)
    const refunds = getRefundSummary(payments)
    const downgradeRefunds = getDowngradeRefundSummary(order.refunds)
    const loyalty = getLoyaltyPaymentSummary(payments)
    const giftCard = getGiftCardPaymentSummary(payments)
    const paid = getPaymentSummary(payments, hasDowngradedItem)
    const credits = getCreditPaymentSummary(payments, hasDowngradedItem)
    subTotalCalc = paid + promo + voucher
    creditTotalCalc = credits
    if (isCustomOffer && customOfferDeposit) {
      subTotalCalc += customOfferDeposit
    }
    return {
      date: moment(date),
      subTotal: subTotalCalc,
      paidTotal: !hasDowngradedItem ? paid - credits - loyalty - giftCard : paid - loyalty - giftCard,
      promoTotal: config.agentHub.isEnabled ? 0 : promo,
      hasPromoPayment,
      creditTotal: creditTotalCalc,
      refundTotal: !hasDowngradedItem ? refunds : refunds - downgradeRefunds,
      commissionTotal: config.agentHub.isEnabled ? promo : 0,
      giftCardTotal: giftCard,
      voucherTotal: voucher,
    }
  })

  const payOnPickupFees = sum(order.carHireItems, item => item.reservation?.payOnPickUpAmount ?? 0)
  const propertyFees =
    sum(
      order.bedbankItems.filter(item => item.status === 'completed'),
      item => item.offer.packages[0].rate.totals.propertyFees ?? 0,
    ) +
    sum(
      order.items.filter(item => item.status === 'completed'),
      item => item.reservation?.propertyFees ?? 0,
    )

  const dueAtProperty = sum(order.items, item => item.reservation?.extraGuestSurchargePayableAtProperty ?? 0)
  const accommodationTotal = sum(order.items, item => item.total) + sum(order.bedbankItems, item => item.total)
  const deliveryFeeTotal = sum(order.merchandiseShippingItems, item => item.total)

  // This is a hack to get the amounts out of the order
  // if changes to cheaper date were performed
  // When the surcharge is downgraded we make full refund on surcharges and then
  // make a new payment for the new surcharge.
  // This information is internal and should not be exposed to the user.
  let cheaperDateRefund: number, cheaperDatePayment: number, cheaperDateCredits: number
  let downgradeRefund = 0
  cheaperDateRefund = cheaperDatePayment = cheaperDateCredits = 0
  if (order.refunds.length > 0) {
    order.refunds.forEach(refund => {
      if (refund.meta) {
        if (refund.meta.reason === 'Customer changed to cheaper dates') {
          cheaperDateRefund += refund.meta.cashAmount
          cheaperDateCredits += refund.meta.cashAmount
          const paymentTransactionKey = refund.meta.comment.split(' ')[0]
          const payment = order.payments.find(payment => payment.transaction_key === paymentTransactionKey)
          if (payment) {
            cheaperDateRefund -= payment.amount
          }
        }
        if (refund.meta.reason === 'Up/Downgrade voucher') {
          downgradeRefund += refund.meta.cashAmount
        }
      }
    })
  }
  cheaperDatePayment = cheaperDateCredits - cheaperDateRefund

  const isIncompletePayLater = isInitialDepositOrder || isPendingInstalmentOrder || isPendingReserveForZero || isPendingPaymentScheduleOrder
  // For upgrades (hasDowngradedItem), don't subtract creditTotal as it's already accounted for in payments.
  // This applies to ALL isIncompletePayLater orders (deposit, instalment, reserveForZero, payment schedule)
  // when hasDowngradedItem is true, matching the logic for completed upgrades where paidTotal doesn't
  // subtract credits (line 208). Without this, negative credit refunds would be subtracted (thus added back)
  // to the total, causing incorrect Total display (e.g., 1798 - (-761.87) = 2559.87 instead of 1810.37).
  const creditTotalToSubtract = hasDowngradedItem ? 0 : sum(paymentGroups, group => group.creditTotal)
  // if incomplete deposit, instalment and reserveForZero orders use order total as grand total
  const grandTotal = isIncompletePayLater ? order.total - creditTotalToSubtract - sum(paymentGroups, group => group.promoTotal) - sum(paymentGroups, group => group.voucherTotal) + merchantFee : sum(paymentGroups, group => group.paidTotal) - cheaperDateRefund
  // if incomplete deposit or instalment order user order total minus credits total and promo total as sub total
  let subTotal = isIncompletePayLater ? order.total : sum(paymentGroups, group => group.subTotal) - cheaperDatePayment - merchantFee - deliveryFeeTotal

  // car hire addons
  if (order.carHireItems[0]?.reservation) {
    subTotal += sum(order.carHireItems[0].reservation?.addons || [], addons => addons.total)
    if (carHireReservationTotalCharge) {
      subTotal += carHireReservationTotalCharge?.discountAmount ?? 0
      discountAmount += carHireReservationTotalCharge?.discountAmount ?? 0
      discountPercentage += carHireReservationTotalCharge?.discountPercentage ?? 0
    }
  }

  const insuranceAppDiscountTotal = sum(order.insuranceItems, item => getInsuranceOrBookingProtectionAppDiscountTotal(item))
  const bookingProtectionAppDiscountTotal = sum(order.bookingProtectionItems, item => getInsuranceOrBookingProtectionAppDiscountTotal(item))
  const insurancesAppDiscountTotal = insuranceAppDiscountTotal + bookingProtectionAppDiscountTotal
  if (insurancesAppDiscountTotal > 0) {
    subTotal += insurancesAppDiscountTotal
  }

  const hasPromoPayment = paymentGroups.some(group => group.hasPromoPayment)
  const refundedTotal = !hasDowngradedItem ? sum(paymentGroups, group => group.refundTotal) - cheaperDatePayment : sum(paymentGroups, group => group.refundTotal) - downgradeRefund
  const paidTotal = sum(paymentGroups, group => group.paidTotal)

  // payment schedules have service fees added to the order total but shouldn't be included in the subtotal (it is listed as a separate line item)
  if (isPendingPaymentScheduleOrder) {
    const serviceFeeAmount = order.paymentScheduleDetails?.charges.find(schedule => schedule.feeAmount > 0)?.feeAmount
    if (serviceFeeAmount) {
      subTotal -= serviceFeeAmount
    }
  }

  // TODO adapt for other item types with app discount
  const experiencesItemsWithMobileAppDiscount = order.experienceItems.filter(item => item.mobileAppDiscountAmount > 0)
  const transfersItemsWithMobileAppDiscount = order.transferItems.filter(item => item.mobileAppDiscountAmount > 0)

  const totalItemsWithMobileAppDiscount = [...experiencesItemsWithMobileAppDiscount, ...transfersItemsWithMobileAppDiscount]
  const mobileAppDiscountAmount = sum(totalItemsWithMobileAppDiscount, (item) => item.mobileAppDiscountAmount ?? 0)
  const mobileAppDiscountPercentage = sum(totalItemsWithMobileAppDiscount, (item) => item.mobileAppDiscountPercentage ?? 0) / totalItemsWithMobileAppDiscount.length

  return {
    grandTotal,
    subTotal,
    paidTotal,
    refundedTotal,
    hasPromoPayment,
    insuranceAppDiscountTotal,
    payOnPickupFees,
    customOfferDeposit,
    bookingProtectionAppDiscountTotal,
    promoTotal: sum(paymentGroups, group => group.promoTotal),
    voucherTotal: sum(paymentGroups, group => group.voucherTotal),
    commissionTotal: sum(paymentGroups, group => group.commissionTotal),
    giftCardTotal: sum(paymentGroups, group => group.giftCardTotal),
    creditsTotal: sum(paymentGroups, group => group.creditTotal) - cheaperDatePayment,
    paymentGroups: sortBy(paymentGroups, g => g.date.valueOf(), 'asc'),
    deliveryFeeTotal,
    hasDowngradedItem,
    discountAmount,
    discountPercentage,
    merchantFee,
    propertyFees,
    dueAtProperty,
    accommodationTotal,
    mobileAppDiscount: {
      experiences: {
        amount: sum(experiencesItemsWithMobileAppDiscount, item => item.mobileAppDiscountAmount),
        percentage: experiencesItemsWithMobileAppDiscount.length ? sum(experiencesItemsWithMobileAppDiscount, item => item.mobileAppDiscountPercentage) / experiencesItemsWithMobileAppDiscount.length : 0,
      },
      transfers: {
        amount: sum(transfersItemsWithMobileAppDiscount, item => item.mobileAppDiscountAmount),
        percentage: transfersItemsWithMobileAppDiscount.length ? sum(transfersItemsWithMobileAppDiscount, item => item.mobileAppDiscountPercentage) / transfersItemsWithMobileAppDiscount.length : 0,
      },
      totalAmount: mobileAppDiscountAmount,
      totalPercentage: mobileAppDiscountPercentage,
    },
  }
}
